import { FC, useEffect } from "react";

import { observer } from "mobx-react";
import { useDispatch, useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Box, VBox } from "@/components-v2/Box";
import { isFailure, isOk, isPending } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { Button, LinkButton } from "@/components-v2/Button";

import { Dialog } from "@/components-v2/Dialog";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { Tabs } from "@/components-v2/Tabs";
import { MTextInput } from "@/components-v2/TextInput";

import { TMessage } from "@/i18n/tt";
import projectTemplatesImg from "@/assets/img/projectTemplates.svg";
import { ProjectTemplateDTO, TemplateDTO, TemplateType } from "@/templates/templateDomain";
import {
  accountAdminTemplateActions,
  closeAccountTemplates,
  filterTabProps,
  hasTemplatesInFilter,
  listedTemplates,
  openAccountTemplates,
  openTemplateEvents,
  templateFilterOf,
  templateRowMenuItems,
  templatesEmptyState,
} from "@/templates/templateListDomain";
import {
  canCreateFromScope,
  closeTemplateDialog,
  closeTemplatePanel,
  companyAdminTemplateActions,
  createProjectFromTemplate,
  templateInScope,
  templatesIcon,
  templateSubtitle,
  TemplateViewContext,
} from "@/templates/templatePanelDomain";
import {
  ProjectTemplateDialogState,
  savedTemplates,
  SelectedTemplateState,
  TemplatePreviewCompany,
  TemplateState,
} from "@/templates/templateStateDomain";
import { DropdownMenuItems } from "@/components-v2/Dropdown.tsx";

import { TemplateAccount } from "@/templates/saveTemplateDomain";
import { TemplatePanel } from "@/templates/templatePanelComponents";

export const ProjectTemplateDialog = observer(() => {
  const { templates } = useRootStore();
  const source = templates?.source;
  if (!templates || source?.type !== "company" || !source.dialog) return null;
  return (
    <Dialog
      title={source.dialog.title}
      onCancel={closeTemplateDialog()}
      fullscreen
      actions={
        <TemplateDialogActions orgNo={source.orgNo} dialog={source.dialog} state={templates} />
      }
    >
      <ProjectTemplatesView
        state={templates}
        context={{
          type: "company",
          companyAdminActions: companyAdminTemplateActions(templates),
        }}
      />
    </Dialog>
  );
});

const TemplateDialogActions: FC<{
  orgNo: string;
  dialog: ProjectTemplateDialogState;
  state: TemplateState;
}> = observer(({ orgNo, dialog, state }) => {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"end"} alignItems={"center"} gap={"lg"}>
      <LinkButton
        label={t("cancel")}
        onClick={state.selected ? closeTemplatePanel() : closeTemplateDialog()}
      />
      <TemplateDialogPrimaryAction orgNo={orgNo} dialog={dialog} selected={state.selected} />
    </Box>
  );
});

const TemplateDialogPrimaryAction: FC<{
  orgNo: string;
  dialog: ProjectTemplateDialogState;
  selected?: SelectedTemplateState;
}> = observer(({ orgNo, dialog, selected }) => {
  switch (selected?.type) {
    case TemplateType.PROJECT:
      return (
        <CreateProjectButton orgNo={orgNo} dialog={dialog} template={templateInScope(selected)} />
      );
    case TemplateType.MAP:
    case undefined:
      return null;
  }
});

const CreateProjectButton: FC<{
  orgNo: string;
  dialog: ProjectTemplateDialogState;
  template: ProjectTemplateDTO;
}> = observer(({ orgNo, dialog, template }) => {
  const { t } = useTranslation();
  const scope = dialog.scope?.templateId === template.id ? dialog.scope : undefined;
  return scope ? (
    <Button
      variant={"primary"}
      arrow={"right"}
      label={t("dbot.create-project")}
      disabled={!canCreateFromScope(template, scope)}
      loading={dialog.isCreating}
      onClick={createProjectFromTemplate(orgNo, template)}
    />
  ) : null;
});

export const AccountTemplates: FC<{
  account: TemplateAccount;
  companies: TemplatePreviewCompany[];
  typeSegment?: string;
  filterSegment?: string;
  templateId?: string;
  templates?: TemplateState;
}> = observer(({ account, companies, typeSegment, filterSegment, templateId, templates }) => {
  const dispatch = useDispatch();
  const language = useLanguage();
  useEffect(() => {
    dispatch(
      openAccountTemplates(
        account.id,
        templateFilterOf(typeSegment, filterSegment),
        companies,
        templateId,
      ),
    );
  }, [dispatch, account.id, typeSegment, filterSegment, companies, templateId]);
  useEffect(() => () => dispatch(closeAccountTemplates()), [dispatch, account.id]);
  return templates ? (
    <ProjectTemplatesView
      state={templates}
      context={{
        type: "account",
        accountName: account.name,
        accountAdminActions: accountAdminTemplateActions(templates, account, language),
        companies,
      }}
    />
  ) : null;
});

export const ProjectTemplatesView: FC<{
  state: TemplateState;
  context: TemplateViewContext;
}> = observer(({ state, context }) => (
  <ResizableSidepanelLayout
    containerProps={{ overflow: "hidden" }}
    centerPanel={<TemplatesMainPanel state={state} context={context} />}
    rightBaseSizePercent={context.type === "company" ? 60 : 50}
    rightPanel={
      state.selected && (
        <TemplatePanel
          key={state.selected.template.id}
          state={state}
          selected={state.selected}
          context={context}
        />
      )
    }
  />
));

const TemplatesMainPanel: FC<{
  state: TemplateState;
  context: TemplateViewContext;
}> = observer(({ state, context }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const result = state.templatesResult;
  const hasTemplates = savedTemplates(state).length > 0;
  const openTemplate = openTemplateEvents(state, context);
  return (
    <VBox fullheight alignItems={"stretch"}>
      {hasTemplates && context.type === "company" && (
        <VBox bg={"default"} borderBottom alignItems={"stretch"}>
          <VBox container maxWidth={"lg"} paddingX={"lg"} paddingTop={"lg"} alignItems={"stretch"}>
            <Tabs tabs={filterTabProps(state)} />
          </VBox>
        </VBox>
      )}
      <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"} alignItems={"stretch"} grow>
        {isPending(result) && <LoadingIndicator />}
        {isFailure(result) && <Alert type={"error"} message={t("general-error")} />}
        {isOk(result) &&
          (hasTemplatesInFilter(state) ? (
            <>
              <Box>
                <MTextInput
                  model={state.search}
                  placeholder={{ key: "search" }}
                  icon={"magnifying-glass"}
                  type={"search"}
                  inline
                />
              </Box>
              <TemplatesTable
                templates={listedTemplates(state)}
                selectedTemplateId={state.selected?.template.id}
                openTemplatePanel={openTemplate && ((template) => dispatch(openTemplate(template)))}
                rowMenuItems={templateRowMenuItems(state, context)}
              />
            </>
          ) : (
            <TemplatesEmptyState {...templatesEmptyState(state, context)} />
          ))}
      </VBox>
    </VBox>
  );
});

const TemplatesEmptyState: FC<{ title?: TMessage; description: TMessage }> = observer(
  ({ title, description }) => (
    <VBox
      grow={"golden"}
      textAlign={"center"}
      justifyContent={"center"}
      alignItems={"center"}
      gap={"lg"}
    >
      <EmptyState
        illustration={<img src={projectTemplatesImg} alt={""} aria-hidden={"true"} width={300} />}
        title={title}
        description={description}
      />
    </VBox>
  ),
);

const TemplatesTable: FC<{
  templates: TemplateDTO[];
  selectedTemplateId?: string;
  openTemplatePanel?: (template: TemplateDTO) => void;
  rowMenuItems?: (template: TemplateDTO) => DropdownMenuItems;
}> = observer(({ templates, selectedTemplateId, openTemplatePanel, rowMenuItems }) => {
  const { t } = useTranslation();

  const tableColumns: TableColumn<TemplateDTO>[] = [
    {
      name: "title",
      align: "left",
      label: t("enterprise-account.templates.title"),
      getSortValue: (row) => row.name,
      getValue: (row) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon {...templatesIcon(row)} />
          <VBox>
            <div>{row.name}</div>
            <Box fg={"subtle"} textSize={"sm"}>
              {t(templateSubtitle(row))}
            </Box>
          </VBox>
        </Box>
      ),
    },
    {
      name: "createdAt",
      align: "left",
      label: t("enterprise-account.templates.created"),
      getSortValue: (row) => row.createdAt,
      getValue: (row) =>
        row.createdByName
          ? t("enterprise-account.templates.createdAt", {
              date: { type: "date", value: row.createdAt },
              name: row.createdByName,
            })
          : { type: "date", value: row.createdAt },
    },
    {
      name: "description",
      align: "right",
      label: "",
      getValue: (row) =>
        row.description ? (
          <InfoButton infoText={row.description} color={"subtle"} icon={"info"} />
        ) : undefined,
    },
  ];
  return (
    <VBox>
      <DataTable
        tableColumns={tableColumns}
        tableData={templates}
        getId={(row) => row.id}
        rowProps={(row) => ({ selected: row.id === selectedTemplateId })}
        onRowClick={openTemplatePanel}
        initialSortBy={"createdAt"}
        initialSortDirection={"desc"}
        rowActions={rowMenuItems && ((row) => [["context-menu", rowMenuItems(row)]])}
      />
    </VBox>
  );
});
